import { Button, Dropdown, Input, Select, Table, Form, Row, Col, message } from 'antd';
import moreIcon from 'assets/images/icon_btn_More.svg';
import deleteAction from 'assets/images/icon_btn_delete_table.svg';
import deleteBatch from 'assets/images/icon_btn_delete_text.svg';
import editAction from 'assets/images/icon_btn_edit_table.svg';
import { useAutoSize } from 'hooks';
import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import * as api from '../../../../../api';
import PaginationComponent from '../../../../../components/pagination';
import { providedByOptions } from '../../../../../constants/knowledge-base';
import * as Permissions from '../../../../../constants/permissions';
import ConfirmModal from '../../../../../components/confirm-modal';
import '../../../index.css';

const Documents = forwardRef(({ appId }, documentRef) => {
	const { permissions } = useSelector((state) => state.user);
	const [ref, tableHeight] = useAutoSize();
	const { Option } = Select;
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
		total: 0,
	});

	const [form] = Form.useForm();

	useImperativeHandle(documentRef, () => ({
		fetchDocumentList: (page, pageSize, appId) => {
			setFilter({
				sbu: null,
				productName: null,
			});
			fetchDocumentList(page, pageSize, appId, true);
		},
	}));

	useEffect(() => {
		fetchDocumentList(pagination.current, pagination.pageSize, appId);
	}, [appId]);

	const [dataSource, setDataSource] = useState([]);
	const [modalVisible, setModalVisible] = useState(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [filter, setFilter] = useState({
		sbu: null,
		productName: null,
	});

	const confirmType = useRef('delete');
	const [selectedItem, setSelectedItem] = useState(null);

	const deleteDocument = async (record) => {
		confirmType.current = 'delete';
		setSelectedItem(record);
		setModalVisible(true);
	};

	const fetchDocumentList = async (page, pageSize, appId, isRest = false) => {
		try {
			const params = {
				pageIndex: page,
				pageSize,
				sbu: isRest ? null : filter.sbu,
				product: isRest ? null : filter.productName?.trim(),
			};
			const response = await api.getDocumentList(appId, params);
			setDataSource(response.data || []);
			setPagination({
				...pagination,
				current: page,
				pageSize,
				total: response.total || 0,
			});
		} catch (error) {
			console.error('Error fetching document list:', error);
		}
	};

	const handleEditDocument = (record) => {
		confirmType.current = 'edit';
		setSelectedItem(record);
		setModalVisible(true);
		form.setFieldsValue(record);
	};

	const onSearch = () => {
		setSelectedRowKeys([]);
		fetchDocumentList(1, pagination.pageSize, appId);
	};

	const onResetFilter = () => {
		setFilter({
			sbu: null,
			productName: null,
		});
		setSelectedRowKeys([]);
		fetchDocumentList(1, 10, appId, true);
	};

	const onBatchDelete = () => {
		confirmType.current = 'batch-delete';
		setModalVisible(true);
	};

	const onSelectChange = (newSelectedRowKeys) => {
		setSelectedRowKeys(newSelectedRowKeys);
	};

	const rowSelection = {
		selectedRowKeys,
		onChange: onSelectChange,
		columnWidth: 40,
	};

	const onConfirm = async () => {
		if (confirmType.current === 'delete') {
			try {
				await api.deleteDocument([selectedItem.id]);
				fetchDocumentList(pagination.current, pagination.pageSize, appId);
			} catch (error) {
				console.error('Error deleting document:', error);
			}
			setModalVisible(false);
		} else if (confirmType.current === 'batch-delete') {
			try {
				await api.deleteDocument(selectedRowKeys);
				fetchDocumentList(pagination.current, pagination.pageSize, appId);
				setSelectedRowKeys([]);
			} catch (error) {
				console.error('Error deleting documents:', error);
			}
			setModalVisible(false);
		} else if (confirmType.current === 'edit') {
			try {
				const values = await form.validateFields();
				await api.updateDocument(selectedItem.id, values);
				fetchDocumentList(pagination.current, pagination.pageSize, appId);
				setModalVisible(false);
			} catch (error) {
				console.log('error', error?.response?.data?.message);
				if (error?.response?.data?.message) {
					message.error(error?.response?.data?.message);
				}
				console.error('Error updating document:', error);
			}
		}
	};

	function _modalTitle() {
		if (confirmType.current === 'batch-delete') {
			return 'Confirm to Batch Delete';
		} else if (confirmType.current === 'delete') {
			return 'Confirm to Delete';
		} else if (confirmType.current === 'edit') {
			return 'Edit Technical Document';
		} else {
			return 'Confirm';
		}
	}

	function _modalContent() {
		if (confirmType.current === 'batch-delete') {
			return (
				<>
					<div>The selected documents will be removed from the technical documents</div>
					<div>Are you sure to delete these documents?</div>
				</>
			);
		} else if (confirmType.current === 'delete') {
			return (
				<>
					<div>The following document will be removed from the technical documents</div>
					<div>{selectedItem?.productName}</div>
				</>
			);
		} else if (confirmType.current === 'edit') {
			return (
				<>
					<Form form={form} initialValues={selectedItem} layout="vertical" className="edit-document-form">
						<Row gutter={12}>
							<Col span={8}>
								<Form.Item
									name="productName"
									label="Product"
									rules={[{ required: true, message: 'Product can not be null' }]}
								>
									<Input placeholder="Please input product" />
								</Form.Item>
							</Col>
							<Col span={8}>
								<Form.Item name="type" label="Type" rules={[{ required: true, message: 'Type can not be null' }]}>
									<Input placeholder="Please input type" />
								</Form.Item>
							</Col>
							<Col span={8}>
								<Form.Item
									name="language"
									label="Language"
									rules={[{ required: true, message: 'Language can not be null' }]}
								>
									<Input placeholder="Please input language" />
								</Form.Item>
							</Col>
							<Col span={24}>
								<Form.Item name="url" label="URL" rules={[{ required: true, message: 'URL can not be null' }]}>
									<Input placeholder="Please input URL" />
								</Form.Item>
							</Col>
						</Row>
					</Form>
				</>
			);
		} else {
			return '';
		}
	}

	const columns = [
		{
			title: 'Product',
			dataIndex: 'productName',
			key: 'productName',
			width: 200,
		},
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
			width: 100,
		},
		{
			title: 'Language',
			dataIndex: 'language',
			key: 'language',
			width: 100,
		},
		{
			title: 'URL',
			dataIndex: 'url',
			key: 'url',
			width: 300,
			render: (text) => <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{text}</div>,
		},
		{
			title: 'Provided by',
			dataIndex: 'sbu',
			key: 'sbu',
			width: 200,
			render: (text) => text.join(', '),
		},
		{
			title: 'Editor',
			dataIndex: 'updatedBy',
			key: 'updatedBy',
			width: 180,
		},
		{
			title: 'Action',
			key: 'action',
			fixed: 'right',
			width: 100,
			render: (_, record) => {
				const menuItems = [];

				if (permissions.includes(Permissions.Document_EDIT)) {
					menuItems.push({
						key: 'action-edit',
						icon: (
							<img
								style={{
									width: 19,
									height: 19,
									marginRight: 10,
									marginLeft: 1,
								}}
								src={editAction}
								alt="edit"
							/>
						),
						label: 'Edit',
						onClick: () => handleEditDocument(record),
					});
				}

				if (permissions.includes(Permissions.Document_DELETE)) {
					menuItems.push({
						key: 'action-delete',
						icon: <img src={deleteAction} alt="delete" style={{ marginRight: 9, marginLeft: 0 }} />,
						label: 'Delete',
						onClick: () => deleteDocument(record),
					});
				}

				if (menuItems.length === 0) {
					return null;
				}
				return (
					<div className="action-drop" style={{ textAlign: 'center' }}>
						<Dropdown
							defaultOpen={true}
							placement="left"
							trigger={['click']}
							style={{ marginLeft: '-80px' }}
							menu={{
								items: menuItems,
							}}
						>
							<div style={{ cursor: 'pointer' }}>
								<img alt="more" src={moreIcon} />
							</div>
						</Dropdown>
					</div>
				);
			},
		},
	];

	return (
		<div style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: '1 1' }}>
			<div className="app-filter-section">
				<div className="filter-items-row">
					<div className="filter-item">
						<Input
							placeholder="Please input product name"
							className="text-input"
							value={filter.productName}
							onChange={(val) => setFilter({ ...filter, productName: val?.target?.value ?? '' })}
							allowClear
						/>
					</div>
					<Select
						placeholder="Provided by"
						value={filter.sbu}
						onChange={(val) => setFilter({ ...filter, sbu: val })}
						className="filter-item"
						allowClear
					>
						{providedByOptions.map((option) => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</Select>
					<div className="filter-actions">
						<Button color="purple" variant="filled" onClick={onSearch}>
							Search
						</Button>
						<Button variant="outlined" onClick={async () => await onResetFilter()}>
							Reset
						</Button>
					</div>
				</div>
			</div>

			{selectedRowKeys.length > 0 && permissions.includes(Permissions.Document_DELETE) && (
				<div className="batch-delete-header">
					<span className="desc">Please select more items to</span>
					<div className="btn" onClick={onBatchDelete}>
						<img src={deleteBatch} alt="batch delete" />
						<span>Batch Delete</span>
					</div>
				</div>
			)}
			<div className="app-table">
				<div className="app-table-content" ref={ref}>
					<Table
						rowKey="id"
						size="small"
						dataSource={dataSource}
						columns={columns}
						rowSelection={permissions.includes(Permissions.Document_DELETE) ? rowSelection : undefined}
						pagination={false}
						scroll={{ x: 'max-content', y: tableHeight }}
					/>
				</div>
				<div className="table-line-all">
					<PaginationComponent
						pagination={pagination}
						onChange={(current, pageSize) => {
							setPagination({ ...pagination, current, pageSize });
							fetchDocumentList(current, pageSize, appId);
						}}
					/>
				</div>
			</div>
			<ConfirmModal
				visible={modalVisible}
				onCancel={() => setModalVisible(false)}
				onConfirm={onConfirm}
				title={_modalTitle()}
				contentElement={_modalContent()}
				width={confirmType.current === 'edit' ? 960 : undefined}
				showIcon={confirmType.current !== 'edit'}
				showDefaultTitle={confirmType.current === 'edit'}
				confirmButtonText={confirmType.current === 'edit' ? 'Save' : 'Confirm'}
			/>
		</div>
	);
});

export default Documents;
